import React, { useState } from "react";
import { Form, Input, Button, Typography, Result } from "antd";
import { LockOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";

const { Title } = Typography;

const LogoMark = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="26" height="26" rx="7" fill="var(--i-ems-accent)" />
    <path d="M7.5 13.5l3.5 3.5 7.5-8" stroke="#0B1330" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ResetPassword = () => {
  const { request } = useRequest();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";
  const token = searchParams.get("token") || "";
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const onFinish = (values) => {
    setError("");
    setSubmitting(true);
    request({
      url: apiPath.resetPassword,
      method: "POST",
      data: { email, token, password: values.password },
      onSuccess: (res) => {
        if (res?.status === 200) {
          setDone(true);
          setTimeout(() => navigate("/login"), 2500);
        } else {
          setError(res?.message || "Could not reset password.");
        }
      },
      onError: (err) => setError(err?.response?.data?.message || "This reset link is invalid or has expired."),
    }).finally(() => setSubmitting(false));
  };

  const missingParams = !email || !token;

  return (
    <div className="i-ems-login-page-single">
      <div className="i-ems-login-form-panel">
        <span className="i-ems-login-form-glow-1" />
        <span className="i-ems-login-form-glow-2" />
        <div className="i-ems-login-card">
          <div className="i-ems-login-logo">
            <LogoMark />
            <span className="i-ems-login-logo-wordmark">I-EMS Admin</span>
          </div>
          {missingParams ? (
            <Result
              status="error"
              title="Invalid reset link"
              subTitle="This link is missing required information. Please request a new reset link."
              extra={
                <Link to="/forgot-password">
                  <Button type="primary">Request new link</Button>
                </Link>
              }
            />
          ) : done ? (
            <Result status="success" title="Password reset!" subTitle="Redirecting you to sign in…" />
          ) : (
            <>
              <Title level={3} className="i-ems-login-title">
                Set a new password
              </Title>
              <div className="i-ems-login-subtitle">Resetting password for {email}</div>
              <Form layout="vertical" onFinish={onFinish}>
                <Form.Item name="password" label="New password" rules={[{ required: true, min: 6, message: "At least 6 characters" }]}>
                  <Input.Password size="large" prefix={<LockOutlined style={{ color: "var(--i-ems-text-secondary)" }} />} placeholder="••••••••" />
                </Form.Item>
                <Form.Item
                  name="confirmPassword"
                  label="Confirm password"
                  dependencies={["password"]}
                  rules={[
                    { required: true, message: "Please confirm your password" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) return Promise.resolve();
                        return Promise.reject(new Error("Passwords do not match"));
                      },
                    }),
                  ]}
                >
                  <Input.Password size="large" prefix={<LockOutlined style={{ color: "var(--i-ems-text-secondary)" }} />} placeholder="••••••••" />
                </Form.Item>
                {error && <div style={{ color: "#EF4444", marginBottom: 16, fontSize: 13 }}>{error}</div>}
                <Form.Item style={{ marginBottom: 0 }}>
                  <Button type="primary" htmlType="submit" size="large" block loading={submitting}>
                    Reset password <ArrowRightOutlined />
                  </Button>
                </Form.Item>
              </Form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
